// © Microsoft Corporation. All rights reserved.

import {
    DesignSystem,
    applyTypeRampConfig,
    highContrastSelector,
    neutralForegroundRest
} from "@microsoft/fast-components-styles-msft";

import { ButtonClassNameContract } from "@microsoft/fast-components-class-name-contracts-msft";
import { ComponentStyles } from "@microsoft/fast-jss-manager";
import { SocialMediaIconClassNameContract } from "./SocialMediaIcon.classnames";
import { toPx } from "@microsoft/fast-jss-utilities";

/** Value for border radius on icons */
const borderRadiusIcons = 2;

/** icon height */
const iconHeight = 40;

/** icon width */
const iconWidth = 40;

/** label width */
const labelWidth = 60;

/**
 * The JSS stylesheet for the social media icon
 * @type {ComponentStyles<SocialMediaIconClassNameContract>}
 * @param {DesignSystem} config - The design system configuration interface
 */
export const SocialMediaIconStyles: ComponentStyles<SocialMediaIconClassNameContract, DesignSystem> = (config: DesignSystem) => {
    return {
        iconContainer: {
            "border-radius": toPx(borderRadiusIcons),
            height: toPx(iconHeight),
            width: toPx(iconWidth),
            "line-height": toPx(iconHeight),
            margin: "0 auto",
            position: "relative",
            "&:hover": {
                cursor: "pointer"
            }
        },
        iconImage: {
            bottom: "0",
            left: "0",
            margin: "auto",
            position: "absolute",
            right: "0",
            top: "0"
        },
        iconLabel: {
            "&&": {
                ...applyTypeRampConfig("t8"),
                "line-height": "20px",
                "text-align": "center",
                "margin-top": "4px",
                padding: "3px 0",
                width: toPx(labelWidth),
                "&:hover": {
                    cursor: "pointer"
                },
                [highContrastSelector]: {
                    background: "ButtonFace",
                    color: "LinkText"
                }
            }
        }
    };
};

/** style to override button existing style */
export const ButtonStyle: Partial<ComponentStyles<ButtonClassNameContract, DesignSystem>> = (config: DesignSystem) => {
    return {
        button__justified: {
            "&&": {
                "background-color": "transparent",
                "text-decoration": "none",
                height: "inherit",
                "min-width": "unset",
                width: "fit-content",
                "&:focus": {
                    outline: `1px solid ${neutralForegroundRest(config)}`
                },
                "& span": {
                    width: "inherit"
                }
            }
        }
    };
};